import { debounce } from "lodash-es"
import { useCallback, useEffect, useRef } from "react"

interface Params {
  onOutsideClick: () => void
  allowSiblings?: boolean
}

const useOutsideClick = ({ onOutsideClick, allowSiblings = false }: Params) => {
  const node = useRef<any>(null)
  const debouncedClickRef = useRef(
    debounce(
      (
        event: MouseEvent | TouchEvent,
        node: any,
        allowSiblings: boolean,
        onOutsideClick: () => void
      ) => {
        const isInsideClick =
          node.current && node.current.contains(event.target)
        const isSiblingClick =
          !isInsideClick &&
          node.current &&
          node.current.parentNode.contains(event.target) &&
          node.current.parentNode !== event.target

        if (
          node.current &&
          (!allowSiblings ? !isInsideClick : !isInsideClick && !isSiblingClick)
        ) {
          onOutsideClick()
        }
      },
      100
    )
  )

  const handleClick = useCallback(
    (e: MouseEvent | TouchEvent) => {
      debouncedClickRef.current(e, node, allowSiblings, onOutsideClick)
    },
    [allowSiblings, onOutsideClick]
  )

  useEffect(() => {
    const debouncedFn = debouncedClickRef.current

    document.addEventListener("mousedown", handleClick)
    document.addEventListener("touchstart", handleClick)

    return () => {
      debouncedFn.cancel()
      document.removeEventListener("mousedown", handleClick)
      document.removeEventListener("touchstart", handleClick)
    }
  }, [handleClick])

  return node
}

export default useOutsideClick
